import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, CheckCircle2, Users, Award, Shield, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { heroContent, quickBenefits } from '@/data/mock';

const socialProof = [
  {
    icon: Users,
    stat: "1,400+",
    label: "Profiles Managed"
  },
  {
    icon: Award,
    stat: "98%",
    label: "Client Satisfaction"
  },
  {
    icon: Shield,
    stat: "DMCC Act",
    label: "Full regulatory expertise"
  },
  {
    icon: Globe,
    stat: "UK • US • India",
    label: "Teams in UK, US, and India"
  }
];

const highlightWords = new Set(['Reviews', 'Feedback', 'Local']);

const renderHeadline = (headline) => {
  const words = headline.split(' ');
  const highlightIndex = words.findIndex((word) => {
    const cleanWord = word.replace(/[.,]/g, '');
    return highlightWords.has(cleanWord);
  });

  return words.map((word, index) => (
    <span
      key={`${word}-${index}`}
      className={index === highlightIndex ? 'gradient-text' : undefined}
    >
      {word}
      {index < words.length - 1 ? ' ' : ''}
    </span>
  ));
};

export const Hero = () => {
  const headlines = useMemo(
    () => [
      'Reviews as Growth Engine. Compliance as Shield',
      'Enterprise Reputation Governance for Global Brands',
      'Turn Reviews into Your Growth Engine - Risk-Free',
      'Turn Feedback into Fuel - Without the Fines',
      'Turn Local Search into Success - Audited & Optimised',
      'Turn Local Presence into Profit - Securely Managed',
      'Executive-Grade Trust Signals. AI-Ready Visibility',
      'Enterprise Reviews, Reduced Risk, Clear Control',
    ],
    [],
  );
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [regionLabel, setRegionLabel] = useState('Global');

  useEffect(() => {
    const interval = setInterval(() => {
      setHeadlineIndex((prev) => (prev + 1) % headlines.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [headlines.length]);

  useEffect(() => {
    const controller = new AbortController();

    const loadRegionLabel = async () => {
      try {
        const response = await fetch('/cdn-cgi/trace', {
          signal: controller.signal,
          cache: 'no-store',
        });
        const text = await response.text();
        const match = text.match(/^loc=([A-Z]{2})$/m);
        const country = match ? match[1] : null;

        if (country === 'GB' || country === 'UK') {
          setRegionLabel('the UK');
        } else if (country === 'US') {
          setRegionLabel('the US');
        } else if (country === 'IN') {
          setRegionLabel('India');
        } else {
          setRegionLabel('Global');
        }
      } catch (error) {
        setRegionLabel('Global');
      }
    };

    loadRegionLabel();

    return () => controller.abort();
  }, []);

  return (
    <section className="relative overflow-hidden bg-ybro-light">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(111,164,169,0.18),transparent_55%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.7),transparent_40%)]" />
      
      {/* Decorative elements */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-ybro-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/50 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-ybro-primary/30 mb-8 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-ybro-primary animate-pulse" />
            <span className="text-sm font-medium font-body text-ybro-dark">
              {regionLabel === 'Global'
                ? 'Trusted Local Search Partner - Global'
                : `Trusted Local Search Partner in ${regionLabel}`}
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-heading text-slate-900 tracking-tight leading-[1.15] mb-6">
            <span key={headlineIndex} className="inline-block animate-headline">
              {renderHeadline(headlines[headlineIndex])}
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl lg:text-2xl text-slate-700 font-medium font-body mb-6">
            {heroContent.subheadline}
          </p>

          {/* Description */}
          <p className="text-lg text-slate-600 font-body leading-relaxed mb-10 max-w-3xl mx-auto">
            {heroContent.description}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-ybro-dark hover:bg-ybro-primary text-white shadow-xl shadow-ybro-primary/25 hover:shadow-ybro-primary/40 transition-all duration-300 text-base px-8 py-6 h-auto font-body font-semibold"
            >
              <Link to="/contact" className="flex items-center gap-2">
                {heroContent.primaryCta}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
            <Button 
              variant="outline"
              size="lg"
              asChild
              className="border-2 border-ybro-dark text-ybro-dark bg-transparent hover:bg-ybro-dark hover:text-white text-base px-8 py-6 h-auto font-body font-semibold transition-all duration-300"
            >
              <a href="tel:+442039967644" className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                {heroContent.secondaryCta}
              </a>
            </Button>
          </div>

          {/* Social Proof Stats */}
          <div className="grid grid-cols-1 min-[340px]:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {socialProof.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div 
                  key={index}
                  className="p-5 rounded-2xl bg-white/80 backdrop-blur-sm border border-white shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-ybro-primary/10 flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-ybro-primary" />
                    </div>
                  </div>
                  <p className="text-xl font-bold font-heading text-slate-900">{item.stat}</p>
                  <p className="text-sm text-slate-600 font-body">{item.label}</p>
                </div>
              );
            })}
          </div>

          {/* Quick Benefits */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
            {quickBenefits.map((benefit, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 p-4 rounded-xl bg-white/70 backdrop-blur-sm border border-white hover:bg-white hover:shadow-lg transition-all duration-300"
              >
                <CheckCircle2 className="w-5 h-5 text-ybro-primary flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 text-sm leading-relaxed font-body">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
